import React from "react";

import type { KeyStat } from "@/pages/Dashboard/components/PortfolioKeyStats";

import type { Program } from "../../shared/programMocks";
import { agentsForProgram } from "../agentsMocks";
import { holdersForProgram } from "../holdersMocks";

import { ProgramKeyStats } from "./ProgramKeyStats";

interface ProgramKeyStatsContainerProps {
    program: Program;
}

/**
 * ProgramKeyStatsContainer — the **Container**. Owns the data seam: derives
 * the program's key-stat figures from its participants (agents or holders, per
 * `program.target`) via the mock helpers, builds a `KeyStat[]`, and renders
 * the props-only `ProgramKeyStats` View with it. Imports the View; the View
 * imports no data (firewall).
 *
 * This is the primary entry `ProgramDetail` imports (same `program` prop as
 * before) — see index.ts.
 */
export function ProgramKeyStatsContainer({
    program
}: ProgramKeyStatsContainerProps): React.ReactElement {
    const isHolders = program.target === "holders";
    // "settled" doubles as holder "spent" — same meter math, target-driven
    // label. Participant count + distributed rewards are sourced the same way.
    // Accrued / projected come off the program's next-distribution cycle — the
    // same figures the header's NextDistributionMeter used to render as its
    // trailing labels (now surfaced as their own key-stat cells). Absent for
    // ended programs (no next distribution) → 0.
    const accrued = program.nextDistribution?.accrued ?? 0;
    const projected = program.nextDistribution?.projected ?? 0;
    const enrolled = isHolders
        ? holdersForProgram(program.id).length
        : agentsForProgram(program.id).length;
    const settled = isHolders
        ? holdersForProgram(program.id).reduce((sum, h) => sum + h.spent, 0)
        : agentsForProgram(program.id).reduce((sum, a) => sum + a.settled, 0);
    const distributed = isHolders
        ? holdersForProgram(program.id).reduce((sum, h) => sum + h.rewards, 0)
        : agentsForProgram(program.id).reduce((sum, a) => sum + a.rewards, 0);

    const stats: KeyStat[] = [
        {
            label: "Total USDv Held",
            value: `$${settled.toLocaleString("en-US")}`
        },
        {
            label: "USDv Accrued",
            value: `$${accrued.toLocaleString("en-US")}`
        },
        {
            label: "USDv Projected",
            value: `$${projected.toLocaleString("en-US")}`
        },
        {
            label: "USDv Distributed",
            value: `$${distributed.toLocaleString("en-US", {
                maximumFractionDigits: 0
            })}`
        },
        { label: "Users", value: enrolled.toLocaleString("en-US") }
    ];

    // Base APY — continuous programs, and fixed-length open-budget programs
    // (which run the period at a base APY, uncapped). Same condition that
    // gated the standalone Base APY cell in the pre-migration meters block.
    if (program.programType === "continuous" || program.budgetMode === "open") {
        stats.push({
            label: "Base APY",
            value: program.baseApy === undefined ? "—" : `${program.baseApy}%`
        });
    }

    return <ProgramKeyStats stats={stats} />;
}
